import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { apiRequest } from "@/lib/api";
import type {
  VendorWithOutstanding,
  ProductWithStock,
  InventoryPurchaseWithDetails,
  VendorFormData,
  ProductFormData,
  PurchaseFormData,
  ClientSupplementFormData,
  ClientSupplementWithDetails,
  LedgerFilters,
  LedgerResponse,
} from "@/types/inventory";

// =============================================
// VENDORS - Async Fetch Functions
// =============================================

async function fetchVendors(
  accessToken: string | null | undefined,
  params?: { is_active?: boolean; search?: string }
) {
  const searchParams = new URLSearchParams();
  if (params?.is_active !== undefined) {
    searchParams.append("is_active", String(params.is_active));
  }
  if (params?.search) {
    searchParams.append("search", params.search);
  }

  const queryString = searchParams.toString();
  const url = `/api/inventory/vendors${queryString ? `?${queryString}` : ""}`;

  return apiRequest<{ vendors: VendorWithOutstanding[] }>(
    url,
    { method: "GET" },
    accessToken
  );
}

async function fetchVendor(
  vendorId: string,
  accessToken: string | null | undefined
) {
  return apiRequest(
    `/api/inventory/vendors/${vendorId}`,
    { method: "GET" },
    accessToken
  );
}

// =============================================
// VENDORS - Hooks
// =============================================

export function useVendors(params?: { is_active?: boolean; search?: string }) {
  const { session } = useAuth();

  return useQuery<{ vendors: VendorWithOutstanding[] }>({
    queryKey: ["vendors", params],
    enabled: Boolean(session),
    queryFn: () => fetchVendors(session?.access_token, params),
  });
}

export function useVendor(vendorId: string) {
  const { session } = useAuth();

  return useQuery({
    queryKey: ["vendor", vendorId],
    enabled: Boolean(vendorId && session),
    queryFn: () => fetchVendor(vendorId, session?.access_token),
  });
}

export function useCreateVendor() {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: VendorFormData) => {
      return apiRequest(
        "/api/inventory/vendors",
        {
          method: "POST",
          body: JSON.stringify(data),
        },
        session?.access_token
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vendors"] });
    },
  });
}

export function useUpdateVendor(vendorId: string) {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Partial<VendorFormData>) => {
      return apiRequest(
        `/api/inventory/vendors/${vendorId}`,
        {
          method: "PATCH",
          body: JSON.stringify(data),
        },
        session?.access_token
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vendors"] });
      queryClient.invalidateQueries({ queryKey: ["vendor", vendorId] });
    },
  });
}

export function useDeleteVendor() {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (vendorId: string) => {
      return apiRequest(
        `/api/inventory/vendors/${vendorId}`,
        { method: "DELETE" },
        session?.access_token
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vendors"] });
    },
  });
}

// =============================================
// PRODUCTS - Async Fetch Functions
// =============================================

async function fetchProducts(
  accessToken: string | null | undefined,
  params?: { is_active?: boolean; search?: string }
) {
  const searchParams = new URLSearchParams();
  if (params?.is_active !== undefined) {
    searchParams.append("is_active", String(params.is_active));
  }
  if (params?.search) {
    searchParams.append("search", params.search);
  }

  const queryString = searchParams.toString();
  const url = `/api/inventory/products${queryString ? `?${queryString}` : ""}`;

  return apiRequest<{ products: ProductWithStock[] }>(
    url,
    { method: "GET" },
    accessToken
  );
}

async function fetchProduct(
  productId: string,
  accessToken: string | null | undefined
) {
  return apiRequest(
    `/api/inventory/products/${productId}`,
    { method: "GET" },
    accessToken
  );
}

// =============================================
// PRODUCTS - Hooks
// =============================================

export function useProducts(params?: { is_active?: boolean; search?: string }) {
  const { session } = useAuth();

  return useQuery<{ products: ProductWithStock[] }>({
    queryKey: ["products", params],
    enabled: Boolean(session),
    queryFn: () => fetchProducts(session?.access_token, params),
  });
}

export function useProduct(productId: string) {
  const { session } = useAuth();

  return useQuery({
    queryKey: ["product", productId],
    enabled: Boolean(productId && session),
    queryFn: () => fetchProduct(productId, session?.access_token),
  });
}

export function useCreateProduct() {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ProductFormData) => {
      return apiRequest(
        "/api/inventory/products",
        {
          method: "POST",
          body: JSON.stringify(data),
        },
        session?.access_token
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
    },
  });
}

export function useUpdateProduct(productId: string) {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Partial<ProductFormData>) => {
      return apiRequest(
        `/api/inventory/products/${productId}`,
        {
          method: "PATCH",
          body: JSON.stringify(data),
        },
        session?.access_token
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
      queryClient.invalidateQueries({ queryKey: ["product", productId] });
    },
  });
}

export function useDeleteProduct() {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (productId: string) => {
      return apiRequest(
        `/api/inventory/products/${productId}`,
        { method: "DELETE" },
        session?.access_token
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
    },
  });
}

// =============================================
// PURCHASES - Async Fetch Functions
// =============================================

async function fetchPurchases(
  accessToken: string | null | undefined,
  params?: {
    vendor_id?: string;
    product_id?: string;
    payment_status?: string;
  }
) {
  const searchParams = new URLSearchParams();
  if (params?.vendor_id) {
    searchParams.append("vendor_id", params.vendor_id);
  }
  if (params?.product_id) {
    searchParams.append("product_id", params.product_id);
  }
  if (params?.payment_status) {
    searchParams.append("payment_status", params.payment_status);
  }

  const queryString = searchParams.toString();
  const url = `/api/inventory/purchases${queryString ? `?${queryString}` : ""}`;

  return apiRequest<{ purchases: InventoryPurchaseWithDetails[] }>(
    url,
    { method: "GET" },
    accessToken
  );
}

async function fetchPurchase(
  purchaseId: string,
  accessToken: string | null | undefined
) {
  return apiRequest(
    `/api/inventory/purchases/${purchaseId}`,
    { method: "GET" },
    accessToken
  );
}

// =============================================
// PURCHASES - Hooks
// =============================================

export function usePurchases(params?: {
  vendor_id?: string;
  product_id?: string;
  payment_status?: string;
}) {
  const { session } = useAuth();

  return useQuery<{ purchases: InventoryPurchaseWithDetails[] }>({
    queryKey: ["purchases", params],
    enabled: Boolean(session),
    queryFn: () => fetchPurchases(session?.access_token, params),
  });
}

export function usePurchase(purchaseId: string) {
  const { session } = useAuth();

  return useQuery({
    queryKey: ["purchase", purchaseId],
    enabled: Boolean(purchaseId && session),
    queryFn: () => fetchPurchase(purchaseId, session?.access_token),
  });
}

export function useCreatePurchase() {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: PurchaseFormData) => {
      return apiRequest(
        "/api/inventory/purchases",
        {
          method: "POST",
          body: JSON.stringify(data),
        },
        session?.access_token
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["purchases"] });
      queryClient.invalidateQueries({ queryKey: ["products"] });
      queryClient.invalidateQueries({ queryKey: ["vendors"] });
    },
  });
}

export function useUpdatePurchase(purchaseId: string) {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Partial<PurchaseFormData>) => {
      return apiRequest(
        `/api/inventory/purchases/${purchaseId}`,
        {
          method: "PATCH",
          body: JSON.stringify(data),
        },
        session?.access_token
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["purchases"] });
      queryClient.invalidateQueries({ queryKey: ["purchase", purchaseId] });
      queryClient.invalidateQueries({ queryKey: ["products"] });
    },
  });
}

export function useDeletePurchase() {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (purchaseId: string) => {
      return apiRequest(
        `/api/inventory/purchases/${purchaseId}`,
        { method: "DELETE" },
        session?.access_token
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["purchases"] });
      queryClient.invalidateQueries({ queryKey: ["products"] });
    },
  });
}

// =============================================
// CLIENT SUPPLEMENTS - Async Fetch Functions
// =============================================

async function fetchClientSupplements(
  clientId: string,
  accessToken: string | null | undefined
) {
  return apiRequest<{ supplements: ClientSupplementWithDetails[] }>(
    `/api/clients/${clientId}/supplements`,
    { method: "GET" },
    accessToken
  );
}

// =============================================
// CLIENT SUPPLEMENTS - Hooks
// =============================================

export function useClientSupplements(clientId: string) {
  const { session } = useAuth();

  return useQuery<{ supplements: ClientSupplementWithDetails[] }>({
    queryKey: ["client-supplements", clientId],
    enabled: Boolean(clientId && session),
    queryFn: () => fetchClientSupplements(clientId, session?.access_token),
  });
}

export function useCreateClientSupplement(clientId: string) {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ClientSupplementFormData) => {
      return apiRequest(
        `/api/clients/${clientId}/supplements`,
        {
          method: "POST",
          body: JSON.stringify(data),
        },
        session?.access_token
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["client-supplements", clientId],
      });
      queryClient.invalidateQueries({ queryKey: ["products"] });
      queryClient.invalidateQueries({ queryKey: ["payments", clientId] });
      queryClient.invalidateQueries({ queryKey: ["reminders"] });
      queryClient.invalidateQueries({ queryKey: ["timeline", clientId] });
    },
  });
}

export function useUpdateClientSupplement(clientId: string) {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      supplementId,
      data,
    }: {
      supplementId: string;
      data: Partial<ClientSupplementFormData>;
    }) => {
      return apiRequest(
        `/api/clients/${clientId}/supplements/${supplementId}`,
        {
          method: "PATCH",
          body: JSON.stringify(data),
        },
        session?.access_token
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["client-supplements", clientId],
      });
      queryClient.invalidateQueries({ queryKey: ["timeline", clientId] });
      queryClient.invalidateQueries({ queryKey: ["payments", clientId] });
    },
  });
}

export function useDeleteClientSupplement(clientId: string) {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (supplementId: string) => {
      return apiRequest(
        `/api/clients/${clientId}/supplements/${supplementId}`,
        { method: "DELETE" },
        session?.access_token
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["client-supplements", clientId],
      });
      queryClient.invalidateQueries({ queryKey: ["products"] });
      queryClient.invalidateQueries({ queryKey: ["timeline", clientId] });
    },
  });
}

// =============================================
// LEDGER - Async Fetch Functions
// =============================================

async function fetchLedger(
  accessToken: string | null | undefined,
  filters: LedgerFilters,
  page: number = 1
): Promise<LedgerResponse> {
  const searchParams = new URLSearchParams();

  if (filters.product_id) searchParams.append("product_id", filters.product_id);
  if (filters.vendor_id) searchParams.append("vendor_id", filters.vendor_id);
  if (filters.client_id) searchParams.append("client_id", filters.client_id);
  if (filters.transaction_type && filters.transaction_type !== "all") {
    searchParams.append("transaction_type", filters.transaction_type);
  }
  if (filters.date_from) searchParams.append("date_from", filters.date_from);
  if (filters.date_to) searchParams.append("date_to", filters.date_to);

  searchParams.append("limit", "10");
  searchParams.append("offset", String((page - 1) * 10));

  return apiRequest<LedgerResponse>(
    `/api/inventory/ledger?${searchParams.toString()}`,
    { method: "GET" },
    accessToken
  );
}

// =============================================
// LEDGER - Hooks
// =============================================

export function useLedger(filters: LedgerFilters, page: number = 1) {
  const { session } = useAuth();

  return useQuery<LedgerResponse>({
    queryKey: ["inventory-ledger", filters, page],
    enabled: Boolean(session),
    queryFn: () => fetchLedger(session?.access_token, filters, page),
  });
}
